import type { RuleSet } from 'styled-components';

import styled, { css } from 'styled-components';

import { colors, media } from '../../../../shared/core/styles';
import { SiteType } from '~/utilities/graphql/codegen';

const GridPriceValueStyles = css<{ $hasDiscountPrice: boolean }>`
  margin-right: ${({ theme }) => theme.padding['3xs']};
  font-weight: ${({ $hasDiscountPrice, theme }) =>
    $hasDiscountPrice ? theme.variables.font.weight.bold : theme.variables.font.weight.medium};
  font-size: ${({ theme }) => theme.font.size[3]};
`;

const AafPriceValueStyles = css<{ $hasDiscountPrice: boolean }>`
  font-size: 14px;
  font-weight: ${({ $hasDiscountPrice, theme }) =>
    $hasDiscountPrice ? theme.variables.font.weight.bold : theme.variables.font.weight.regular};
  letter-spacing: 0.14px;
`;

const GstarProductPriceWrapper = css``;

const AafProductPriceWrapper = css`
  min-height: 56px;
  margin: 8px 0;
  color: ${colors.NERO_GREY};
`;

const GstarPriceInfo = css`
  line-height: ${({ theme }) => theme.font.size[6]};
`;

const AafPriceInfo = css`
  height: 24px;
  font-style: normal;
  line-height: 16px;
`;

const PriceValueConfig: Record<SiteType, RuleSet<{ $hasDiscountPrice: boolean }>> = {
  [SiteType.Gstar]: GridPriceValueStyles,
  [SiteType.Wahts]: GridPriceValueStyles,
  [SiteType.Outlet]: GridPriceValueStyles,
  [SiteType.Employeeshop]: GridPriceValueStyles,
  [SiteType.Aaf]: AafPriceValueStyles,
};

const PriceInfoConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarPriceInfo,
  [SiteType.Wahts]: GstarPriceInfo,
  [SiteType.Outlet]: GstarPriceInfo,
  [SiteType.Employeeshop]: GstarPriceInfo,
  [SiteType.Aaf]: AafPriceInfo,
};

const GstarPriceOriginal = css`
  font-weight: ${({ theme }) => theme.variables.font.weight.medium};
`;

const AafPriceOriginal = css`
  font-weight: ${({ theme }) => theme.variables.font.weight.regular};
`;

const PriceOriginalConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarPriceOriginal,
  [SiteType.Outlet]: GstarPriceOriginal,
  [SiteType.Employeeshop]: GstarPriceOriginal,
  [SiteType.Aaf]: AafPriceOriginal,
  [SiteType.Wahts]: GstarPriceOriginal,
};

const ProductPriceWrapperConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarProductPriceWrapper,
  [SiteType.Wahts]: GstarProductPriceWrapper,
  [SiteType.Outlet]: GstarProductPriceWrapper,
  [SiteType.Employeeshop]: GstarProductPriceWrapper,
  [SiteType.Aaf]: AafProductPriceWrapper,
};

export const S = {
  ProductPriceWrapper: styled.div<{
    $lowestPriceDisplay: string;
  }>`
    font-size: ${({ theme }) => theme.font.size[3]};
    line-height: 1;
    max-width: 100%;
    position: relative;
    font-weight: ${({ theme }) => theme.variables.font.weight.bold};
    ${({ theme }) => ProductPriceWrapperConfig[theme.siteType]}

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      /* overflow: hidden; RRP Tooltip require overflow visible */
      padding: 0;
      text-align: left;
    }

    @media ${({ theme }) => media(theme).between('xs', 'lg')} {
      padding: 0;
    }
  `,
  PriceDiscount: styled.span`
    color: ${({ theme }) => theme.colors.content.secondary};
    font-weight: ${({ theme }) => theme.variables.font.weight.medium};
    text-decoration: line-through;
    margin-right: ${({ theme }) => theme.padding['2xs']};
  `,
  PriceValue: styled.strong<{
    $basePriceColor?: string;
    $hasDiscountPrice: boolean;
  }>`
    ${({ $basePriceColor, $hasDiscountPrice, theme }) =>
      $hasDiscountPrice
        ? `color: ${$basePriceColor || theme.colors.content.sale};`
        : `color: ${theme.colors.content.primary};`}

    ${({ theme }) => PriceValueConfig[theme.siteType]}
  `,

  PriceLowest30Days: styled.span`
    ${({ theme }) => css`
      font-size: ${theme.font.size[1]};
      font-weight: ${theme.variables.font.weight.regular};
      line-height: 1.4;
      color: ${theme.colors.content.tertiary};
    `}
  `,

  PriceInfo: styled.div<{ $hasRrpTooltip?: boolean }>`
    ${({ $hasRrpTooltip }) => {
      if (!$hasRrpTooltip) {
        return `
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        `;
      }

      return '';
    }}
    display:flex;
    align-items: center;
    flex-wrap: wrap;
    position: relative;
    font-size: ${({ theme }) => theme.font.size[4]};
    font-style: normal;
    line-height: 16px;
    ${({ theme }) => PriceInfoConfig[theme.siteType]}

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      justify-content: start;
    }
  `,

  PriceOriginal: styled.span<{ $isOnlyLowestPrice?: boolean | null }>`
    text-decoration: line-through;
    color: ${({ theme }) => theme.colors.content.secondary};
    font-size: ${({ theme }) => theme.font.size[3]};
    ${({ theme }) => PriceOriginalConfig[theme.siteType]}

    @media ${({ theme }) => media(theme).greaterThan('xs')} {
      margin-right: ${({ $isOnlyLowestPrice, theme }) =>
        $isOnlyLowestPrice ? '0' : theme.padding['2xs']};
    }
  `,

  TooltipContainer: styled.span`
    position: relative;
    display: inline-block;
    line-height: 1.2;
  `,
  IconArrow: styled.span`
    position: relative;
    display: block;
    top: 0;
    left: 0;
    z-index: 55;
    transform: rotate(180deg);

    &::before,
    &::after {
      position: absolute;
      display: block;
      content: '';
      border: 10px solid transparent;
      border-bottom: none;
    }

    &::before {
      top: -10px;
      border-top-color: ${colors.DISABLED_GREY};
    }

    &::after {
      top: -12px;
      border-top-color: ${colors.WHITE};
    }
  `,
};
